'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { X } from 'lucide-react'
import type { ShortFeedItem } from '@/lib/news-types'
import {
  formatEditorDisplayName,
  formatEditorHandleDistinct,
  getAuthorProfileHref,
} from '@/lib/author-url'
import { formatShortCount } from '@/lib/shorts-slug'
import { formatSiteDate } from '@/lib/site-datetime'
import styles from '@/components/shorts/shorts.module.css'

interface ShortsDescriptionPanelProps {
  item: ShortFeedItem
  open: boolean
  onClose: () => void
}

function formatShortDate(value?: string): string {
  if (!value) return ''
  try {
    return formatSiteDate(value, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

export default function ShortsDescriptionPanel({
  item,
  open,
  onClose,
}: ShortsDescriptionPanelProps) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  const bodyText =
    item.description?.trim() ||
    item.titleSub?.trim() ||
    item.title_short?.trim() ||
    item.title

  const published = formatShortDate(item.createdAt)
  const editorHandle = item.editor ? formatEditorHandleDistinct(item.editor) : null

  return (
    <div className={styles.descBackdrop} role="presentation" onClick={onClose}>
      <aside
        className={styles.descPanel}
        role="dialog"
        aria-modal="true"
        aria-label="Video açıklaması"
        data-shorts-control
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <div className={styles.descHeader}>
          <h3 className={styles.descTitle}>Açıklama</h3>
          <button
            type="button"
            data-shorts-control
            className={styles.descClose}
            onClick={onClose}
            aria-label="Açıklamayı kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className={styles.descBody}>
          {item.editor ? (
            <div className={styles.descAuthorBlock}>
              <span className={styles.descAuthorLabel}>
                {item.source === 'youtube' ? 'Kanal' : 'Ekleyen'}
              </span>
              {item.source === 'youtube' && item.youtubeChannel?.url ? (
                <a
                  href={item.youtubeChannel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.descAuthorLink}
                  data-shorts-control
                >
                  {formatEditorDisplayName(item.editor) || item.youtubeChannel.name}
                </a>
              ) : getAuthorProfileHref(item.editor) ? (
                <Link
                  href={getAuthorProfileHref(item.editor)!}
                  className={styles.descAuthorLink}
                  data-shorts-control
                >
                  {formatEditorDisplayName(item.editor)}
                  {editorHandle ? (
                    <span className={styles.descAuthorHandle}>{editorHandle}</span>
                  ) : null}
                </Link>
              ) : (
                <span className={styles.descAuthorLink}>
                  {formatEditorDisplayName(item.editor)}
                </span>
              )}
            </div>
          ) : null}

          <h4 className={styles.descHeadline}>{item.title}</h4>

          <div className={styles.descStats}>
            <div className={styles.descStat}>
              <strong>{formatShortCount(item.commentCount)}</strong>
              <span>Yorum</span>
            </div>
            {published ? (
              <div className={styles.descStat}>
                <strong>{published}</strong>
                <span>Yayın</span>
              </div>
            ) : null}
          </div>

          <p className={styles.descText}>{bodyText}</p>
        </div>
      </aside>
    </div>
  )
}
